import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-324cd957"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "stake-enter-amount__header" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h1", { class: "stake-enter-amount__title" }, "Enter staking amount", -1))
const _hoisted_3 = { class: "stake-enter-amount__info" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "You keep ownership of bonded assets.", -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, " Staking rewards start to show in 2-3 days after confirming a transaction. ", -1))
const _hoisted_6 = { class: "stake-enter-amount__lock" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Lock rewards for compounding?", -1))

import WhiteWrapper from "@/components/white-wrapper/index.vue";
import ButtonsBlock from "@/components/buttons-block/index.vue";
import BaseButton from "@/components/base-button/index.vue";
import BackButton from "@/components/back-button/index.vue";
import InfoTooltip from "@/components/info-tooltip/index.vue";
import Switch from "@/components/switch/index.vue";
import SelectAccountInput from "@/components/select-account-input/index.vue";
import AmountInput from "@/components/amount-input/index.vue";
import { Account } from "@/types/account";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  accounts,
  apiPromise,
  nativeBalances,
  nativeToken,
  selectedNetwork,
  stakingWizardOptions,
} from "@/stores";
import { useGetNativeBalances } from "@/libs/balances";
import { useGetNativePrice } from "@/libs/prices";
import { isValidDecimals, toBase } from "@/utils/units";
import { toBN } from "web3-utils";
import { stakeExtrinsic } from "@/utils/extrinsic";
import { GasFeeInfo } from "@/types/transaction";
import { getGasFeeInfo } from "@/utils/fee";
import { queryHasStash, queryMinNominatorBond } from "@/utils/staking";
import { unbondDuration } from "@/stores";

const rewardsInfo =
  "If you choose not to lock your rewards, then your newly minted rewards will be transferrable by default. However, this would mean lower earnings over longer period of time.";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const router = useRouter();

const fromAccount = ref<Account>(accounts.value[0]);
const hasStash = ref(false);
const minNominatorBond = ref(0);
const amount = ref<number>(0);
const fee = ref<GasFeeInfo>();
const isCompounding = ref(true);
const hasEnough = ref(true);

onMounted(() => {
  loadFeeInfo();
  useGetNativeBalances();
  useGetNativePrice();
  updateMinNominatorBond();
  updateHasStash();
  loadPreviousStakingOptions();
});

watch([accounts], () => {
  useGetNativeBalances();
  useGetNativePrice();
  updateMinNominatorBond();
  fromAccount.value = accounts.value[0];
});

watch([selectedNetwork], () => {
  useGetNativeBalances();
  useGetNativePrice();
  updateMinNominatorBond();
});

watch(fromAccount, async () => {
  updateHasStash();
});

watch(
  [amount, nativeBalances, fromAccount],
  async () => {
    if (!amount.value || !fromAccount.value?.address) {
      return;
    }

    if (!isValidDecimals(amount.value.toString(), nativeToken.value.decimals)) {
      hasEnough.value = false;
      return;
    }

    const rawAmount = toBN(
      toBase(amount.value?.toString() || "0", nativeToken.value.decimals)
    );

    const rawBalance = toBN(
      toBase(
        nativeBalances[fromAccount.value.address]?.available.toString() || "0",
        nativeToken.value.decimals
      )
    );

    if (rawAmount.gt(rawBalance)) {
      hasEnough.value = false;
    } else {
      hasEnough.value = true;
    }
  },
  { deep: true }
);

const loadFeeInfo = async () => {
  // Load fee info for transaction with mock values
  const api = await apiPromise.value;

  const tx = await stakeExtrinsic(
    api,
    accounts.value[0].address,
    "1",
    [accounts.value[0].address],
    true
  );

  fee.value = await getGasFeeInfo(tx, accounts.value[0].address);
};

watch([selectedNetwork, accounts], loadFeeInfo);

const minValueErrorMessage = computed(() => {
  if (hasStash.value) {
    return "";
  }

  if (minNominatorBond.value > amount.value) {
    return `Minimum staking amount is ${
      minNominatorBond.value
    } ${nativeToken.value.symbol.toLocaleUpperCase()}`;
  }

  return "";
});

const updateHasStash = async () => {
  hasStash.value = false;
  const api = await apiPromise.value;
  hasStash.value = await queryHasStash(api, fromAccount.value.address);
};

const updateMinNominatorBond = async () => {
  const api = await apiPromise.value;
  minNominatorBond.value = await queryMinNominatorBond(api);
};

const loadPreviousStakingOptions = () => {
  isCompounding.value = stakingWizardOptions.value.isCompounding;
  if (stakingWizardOptions.value.amount) {
    amount.value = stakingWizardOptions.value.amount;
  }
};

const isValid = computed<boolean>(() => {
  return (
    !!fromAccount.value &&
    Number(amount.value) > 0 &&
    hasEnough.value &&
    !minValueErrorMessage.value
  );
});

const availableBalance = computed(() => {
  if (!nativeBalances || !fromAccount.value) {
    return 0;
  }

  return nativeBalances[fromAccount.value.address]?.available.toNumber() || 0;
});

const nextAction = () => {
  stakingWizardOptions.value = {
    ...stakingWizardOptions.value,
    fromAccount: fromAccount.value,
    isCompounding: isCompounding.value,
    amount: amount.value,
  };
  router.push({
    name: "stake-nominate",
  });
};

const back = () => {
  router.go(-1);
};

const updateCompounding = (newValue: boolean) => {
  isCompounding.value = newValue;
};

const selectAccount = (account: Account) => {
  fromAccount.value = account;
};

const inputAmount = (newVal: string) => {
  amount.value = Number(newVal);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(WhiteWrapper, { class: "stake-enter-amount__wrap" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(BackButton, { action: back }),
        _hoisted_2
      ]),
      _createElementVNode("div", _hoisted_3, [
        _hoisted_4,
        _hoisted_5,
        _createElementVNode("p", null, " Assets will be available again " + _toDisplayString(_unref(unbondDuration)) + " after you decided to withdraw them. ", 1)
      ]),
      _createVNode(SelectAccountInput, {
        account: fromAccount.value,
        accounts: _unref(accounts),
        amount: availableBalance.value,
        "is-amount": true,
        title: "From",
        "onUpdate:select": selectAccount
      }, null, 8, ["account", "accounts", "amount"]),
      _createVNode(AmountInput, {
        "has-enough-balance": hasEnough.value,
        "is-error": !!minValueErrorMessage.value,
        token: _unref(nativeToken),
        value: String(amount.value),
        "max-value": _unref(nativeBalances)[fromAccount.value?.address]?.available,
        "inner-error-message": minValueErrorMessage.value,
        "onUpdate:amount": inputAmount
      }, null, 8, ["has-enough-balance", "is-error", "token", "value", "max-value", "inner-error-message"]),
      _createVNode(ButtonsBlock, { "is-space": true }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(Switch, {
              "is-checked": isCompounding.value,
              "onUpdate:check": updateCompounding
            }, null, 8, ["is-checked"]),
            _hoisted_7,
            _createVNode(InfoTooltip, { text: rewardsInfo })
          ]),
          _createVNode(BaseButton, {
            title: "Continue",
            action: nextAction,
            send: true,
            disabled: !isValid.value
          }, null, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})